/**
 * NotificationBell — header bell icon with unread badge + dropdown preview.
 *
 * Features:
 *   • Animated bell (ring on new notifications)
 *   • Unread count badge (max 99+)
 *   • Dropdown with recent 5 notifications
 *   • Mark read inline
 *   • Mark all read
 *   • View all → notification center
 *   • Close on outside click / Escape
 *   • Admin vs Portal context-aware links
 */

import { useRef, useEffect, useState } from 'react'
import { Link, useLocation }           from 'react-router-dom'
import { motion, AnimatePresence }     from 'framer-motion'
import { Bell, CheckCheck, ArrowRight } from 'lucide-react'

import { ROUTES }               from '@constants/routes'
import { cn }                   from '@utils'
import { useMyNotificationsQuery } from '../api/useNotificationsQuery'
import { useMarkReadMutation, useMarkAllReadMutation } from '../api/useNotificationMutations'
import { NotificationCard }     from './NotificationCard'

// ── Animation variants ─────────────────────────────────────────────────────────

const dropdownVariants = {
  hidden:  { opacity: 0, scale: 0.95, y: -8, transformOrigin: 'top right',
             transition: { duration: 0.12, ease: 'easeIn' } },
  visible: { opacity: 1, scale: 1,    y: 0,
             transition: { duration: 0.2, ease: [0.34, 1.56, 0.64, 1] } },
}

const bellVariants = {
  ring:  { rotate: [0, -15, 15, -10, 10, -5, 5, 0], transition: { duration: 0.6, ease: 'easeInOut' } },
  still: { rotate: 0 },
}

// ── Badge ─────────────────────────────────────────────────────────────────────

function Badge({ count }) {
  if (!count || count <= 0) return null
  return (
    <motion.span
      key={count}
      initial={{ scale: 0.6 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 500, damping: 25 }}
      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold text-white leading-none z-10"
      style={{ background: 'var(--ac-danger)' }}
    >
      {count > 99 ? '99+' : count}
    </motion.span>
  )
}

// ── Dropdown ─────────────────────────────────────────────────────────────────

function NotificationDropdown({ notifications, unreadCount, viewAllHref, onMarkRead, onMarkAllRead, onClose }) {
  const recent = notifications.slice(0, 5)

  return (
    <motion.div
      role="dialog"
      aria-label="Notifications"
      variants={dropdownVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-2xl overflow-hidden z-[400]"
      style={{
        background:  'var(--ac-bg-surface)',
        border:      '1px solid var(--ac-border-base)',
        boxShadow:   'var(--ac-shadow-xl)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: '1px solid var(--ac-border-base)' }}
      >
        <div className="flex items-center gap-2">
          <Bell size={15} strokeWidth={2} style={{ color: 'var(--ac-secondary)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--ac-text-base)' }}>
            Notifications
          </span>
          {unreadCount > 0 && (
            <span
              className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background: 'var(--ac-danger)', color: '#fff' }}
            >
              {unreadCount}
            </span>
          )}
        </div>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="flex items-center gap-1 text-xs font-medium transition-opacity duration-150 hover:opacity-70"
            style={{ color: 'var(--ac-secondary)' }}
          >
            <CheckCheck size={12} strokeWidth={2.5} />
            Mark all read
          </button>
        )}
      </div>

      {/* Notification list */}
      <div className="max-h-[360px] overflow-y-auto scrollbar-theme">
        {recent.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
              style={{ background: 'var(--ac-bg-subtle)' }}
            >
              <Bell size={16} strokeWidth={1.5} style={{ color: 'var(--ac-text-subtle)' }} />
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--ac-text-muted)' }}>
              You&apos;re all caught up!
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--ac-text-subtle)' }}>
              No new notifications
            </p>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            <div className="divide-y" style={{ borderColor: 'var(--ac-border-base)' }}>
              {recent.map((n) => (
                <NotificationCard
                  key={n.id}
                  notification={n}
                  compact
                  onMarkRead={onMarkRead}
                />
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>

      {/* Footer */}
      <div
        className="px-4 py-3"
        style={{ borderTop: '1px solid var(--ac-border-base)' }}
      >
        <Link
          to={viewAllHref}
          onClick={onClose}
          className="flex items-center justify-center gap-1.5 w-full h-8 rounded-xl text-xs font-semibold transition-colors duration-150 hover:bg-[var(--ac-bg-subtle)]"
          style={{ color: 'var(--ac-secondary)' }}
        >
          View all notifications
          <ArrowRight size={12} strokeWidth={2.5} />
        </Link>
      </div>
    </motion.div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function NotificationBell() {
  const [open, setOpen]     = useState(false)
  const [ringing, setRing]  = useState(false)
  const containerRef        = useRef(null)
  const { pathname }        = useLocation()

  const inPortal   = pathname.startsWith(ROUTES.PORTAL.ROOT)
  const viewAllHref = inPortal ? ROUTES.PORTAL.NOTIFICATIONS : ROUTES.ADMIN.NOTIFICATIONS.LIST

  const { data }                           = useMyNotificationsQuery()
  const notifications                      = data?.notifications ?? []
  const unreadCount                        = data?.unreadCount ?? 0
  const { mutate: markRead }               = useMarkReadMutation()
  const { mutate: markAllRead, isPending } = useMarkAllReadMutation()

  // Ring bell when unreadCount goes up
  const prevUnread = useRef(unreadCount)
  useEffect(() => {
    if (unreadCount > prevUnread.current) {
      setRing(true)
      setTimeout(() => setRing(false), 700)
    }
    prevUnread.current = unreadCount
  }, [unreadCount])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onPointerDown(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function handleMarkRead(id) { markRead(id) }
  function handleMarkAllRead() { markAllRead(); setOpen(false) }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        className={cn(
          'relative flex items-center justify-center w-9 h-9 rounded-xl',
          'transition-colors duration-150 outline-none',
          'focus-visible:ring-2 focus-visible:ring-[var(--ac-secondary)]',
          'hover:bg-[var(--ac-bg-subtle)]',
        )}
        style={{ color: 'var(--ac-text-muted)' }}
      >
        <motion.span
          variants={bellVariants}
          animate={ringing ? 'ring' : 'still'}
          className="flex items-center justify-center"
        >
          <Bell size={18} strokeWidth={1.75} />
        </motion.span>
        <Badge count={unreadCount} />
      </button>

      <AnimatePresence>
        {open && (
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            viewAllHref={viewAllHref}
            onMarkRead={handleMarkRead}
            onMarkAllRead={handleMarkAllRead}
            onClose={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
